import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import api from "../../axiosConfig";
// import useReactApexChart from "../../hook/useReactApexChart"; // si lo usas en otro lado, puedes dejarlo

const SERIES_ORDER = ["CFDIS", "XMLS", "RECIBOS"]; // orden fijo para colores/leyenda

const RevenueStatisticOne = () => {
  const [categories, setCategories] = useState([]); // ['2025-07','2025-08', ...]
  const [series, setSeries] = useState([
    { name: "CFDI", data: [] },
    { name: "XML", data: [] },
    { name: "RECIBOS", data: [] },
  ]);
  const [loading, setLoading] = useState(false);

  // Opciones del chart (solo dependen de categories)
  const revenueChartOptionsOne = useMemo(() => ({
    legend: { show: false },
    chart: {
      type: "area",
      width: "100%",
      height: 150,
      toolbar: { show: false },
      padding: { left: 0, right: 0, top: 0, bottom: 0 },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: ["#bbf7d0", "#bfdbfe", "#fef08a"], // CFDIS, XMLS, RECIBOS
      lineCap: "round",
    },
    grid: {
      show: true,
      borderColor: "#D1D5DB",
      strokeDashArray: 1,
      position: "back",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      row: { colors: undefined, opacity: 0.5 },
      column: { colors: undefined, opacity: 0.5 },
      padding: { top: -20, right: 0, bottom: -10, left: 0 },
    },
    fill: {
      type: "gradient",
      colors: ["#bbf7d0", "#bfdbfe", "#fef08a"],
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [undefined, `${"#6593FF"}00`],
        inverseColors: false,
        opacityFrom: [0.4, 0.6],
        opacityTo: [0.3, 0.3],
        stops: [0, 100],
      },
    },
    markers: {
      colors: ["#bbf7d0", "#bfdbfe", "#fef08a"],
      strokeWidth: 2,
      size: 0,
      hover: { size: 8 },
    },
    xaxis: {
      categories,
      tooltip: { enabled: false },
      labels: {
        formatter: (v) => v,
        style: { fontSize: "14px" },
      },
    },
    yaxis: {
      labels: {
        formatter: (v) => v,
        style: { fontSize: "14px" },
      },
    },
    tooltip: {
      x: { formatter: (v) => v }, // categorías tipo 'YYYY-MM'
    },
  }), [categories]);

  const fetchLine = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/admin/line");
      // data esperado:
      // { RECIBOS: [['2025-07',3], ['2025-08',60]], XMLS: [...], CFDIS: [...] }

      // 1) Unimos todas las categorías (meses)
      const monthSet = new Set();
      SERIES_ORDER.forEach((key) => {
        (data[key] || []).forEach(([month]) => monthSet.add(month));
      });

      // 2) Orden ascendente 'YYYY-MM'
      const months = Array.from(monthSet).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      setCategories(months);

      // 3) Map por serie para lookup rápido
      const toMap = (pairs) => {
        const m = new Map();
        (pairs || []).forEach(([month, value]) => m.set(month, Number(value) || 0));
        return m;
      };
      const maps = {
        CFDIS: toMap(data.CFDIS),
        XMLS: toMap(data.XMLS),
        RECIBOS: toMap(data.RECIBOS),
      };

      // 4) Construimos series en el orden fijo
      const finalSeries = [
        {
          name: "CFDI",
          data: months.map((m) => maps.CFDIS.get(m) ?? 0),
        },
        {
          name: "XML",
          data: months.map((m) => maps.XMLS.get(m) ?? 0),
        },
        {
          name: "RECIBOS",
          data: months.map((m) => maps.RECIBOS.get(m) ?? 0),
        },
      ];
      setSeries(finalSeries);
    } catch (e) {
      console.error("Error cargando /dashboard/admin/line:", e);
      // Si quieres, deja categorías/series vacías o las últimas conocidas
      setCategories([]);
      setSeries([
        { name: "CFDI", data: [] },
        { name: "XML", data: [] },
        { name: "RECIBOS", data: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLine();
  }, []);

  return (
    <div className='col-xxl-8'>
      <div className='card h-100'>
        <div className='card-body p-24 mb-8'>
          <h6 className='mb-2 fw-bold text-lg'>Número de descargas</h6>

          <ul className='d-flex flex-wrap align-items-center justify-content-center my-3 gap-24'>
            <li className='d-flex flex-column gap-1'>
              <div className='d-flex align-items-center gap-2'>
                <span className='w-8-px h-8-px rounded-pill bg-success-200' />
                <span className='text-secondary-light text-sm fw-semibold'>CFDIS</span>
              </div>
            </li>
            <li className='d-flex flex-column gap-1'>
              <div className='d-flex align-items-center gap-2'>
                <span className='w-8-px h-8-px rounded-pill bg-info-200' />
                <span className='text-secondary-light text-sm fw-semibold'>XMLS</span>
              </div>
            </li>
            <li className='d-flex flex-column gap-1'>
              <div className='d-flex align-items-center gap-2'>
                <span className='w-8-px h-8-px rounded-pill bg-warning-200' />
                <span className='text-secondary-light text-sm fw-semibold'>Recibos</span>
              </div>
            </li>
          </ul>

          <div id='revenueChart' className='apexcharts-tooltip-style-1'>
            <ReactApexChart
              options={revenueChartOptionsOne}
              series={series}
              type='area'
              height={150}
              width='100%'
            />
          </div>

          {loading && <div className="mt-2 text-sm text-secondary-light">Cargando…</div>}
        </div>
      </div>
    </div>
  );
};

export default RevenueStatisticOne;